import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  X,
  Phone,
  Mail,
  MessageCircle,
  Instagram,
  Facebook,
  Globe,
  Star,
  Tag,
  FileText,
  Clock,
  Calendar,
  ChevronRight,
  Plus,
  User,
  Building2,
  MapPin,
} from "lucide-react";
import { cn } from "@/utils/cn";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import api from "../../../services/api";
import { i18n } from "../../../translate/i18n";
import type { Conversation } from "./InboxList";

const channelIcons = {
  whatsapp: { icon: MessageCircle, color: "#22C55E", label: "WhatsApp" },
  instagram: { icon: Instagram, color: "#E4405F", label: "Instagram" },
  facebook: { icon: Facebook, color: "#1877F2", label: "Facebook" },
  widget: { icon: Globe, color: "#2563EB", label: "Chat Widget" },
};

// Tipo para ticket do histórico
type TicketHistoryItem = {
  id: string;
  title: string;
  status: string;
  date: string;
};

// Tipo para dados do contato
type ContactData = {
  phone: string;
  email: string;
  company: string;
  location: string;
  tags: string[];
  notes: string;
  customFields: { label: string; value: string }[];
  ticketHistory: TicketHistoryItem[];
  stats: {
    totalConversations: number;
    avgResponseTime: string;
    memberSince: string;
    satisfaction: string;
  };
};

const tagColors: Record<string, { bg: string; text: string }> = {
  VIP: { bg: "bg-purple-100", text: "text-purple-700" },
  Enterprise: { bg: "bg-indigo-100", text: "text-indigo-700" },
  Startup: { bg: "bg-emerald-100", text: "text-emerald-700" },
  Lead: { bg: "bg-amber-100", text: "text-amber-700" },
  Cliente: { bg: "bg-blue-100", text: "text-blue-700" },
};

interface ContactPanelProps {
  conversation: Conversation | null;
  isOpen: boolean;
  onClose: () => void;
}

export function ContactPanel({ conversation, isOpen, onClose }: ContactPanelProps) {
  const [contactData, setContactData] = useState<ContactData>({
    phone: "",
    email: "",
    company: "",
    location: "",
    tags: [],
    notes: "",
    customFields: [],
    ticketHistory: [],
    stats: {
      totalConversations: 0,
      avgResponseTime: "-",
      memberSince: "-",
      satisfaction: "-",
    },
  });
  const [loading, setLoading] = useState(false);

  // Buscar dados do contato quando a conversa mudar
  useEffect(() => {
    if (!conversation?.originalTicket?.contactId) return;

    const fetchContactData = async () => {
      setLoading(true);
      try {
        const contactId = conversation.originalTicket.contactId;

        // Buscar dados do contato
        const { data: contact } = await api.get(`/contacts/${contactId}`);

        // Buscar tickets do contato
        const { data: ticketsData } = await api.get(`/tickets`, {
          params: {
            contactId,
            pageNumber: 1,
          },
        });

        const tickets = ticketsData?.tickets || [];

        // Mapear histórico de tickets
        const ticketHistory: TicketHistoryItem[] = tickets.slice(0, 5).map((ticket: any) => ({
          id: `T-${String(ticket.id).padStart(3, "0")}`,
          title: ticket.lastMessage || i18n.t("v2.ticket.noMessages"),
          status: ticket.status === "closed" ? "resolved" : "open",
          date: ticket.createdAt
            ? format(parseISO(ticket.createdAt), "dd/MM/yyyy", { locale: ptBR })
            : "-",
        }));

        // Calcular estatísticas
        const totalConversations = ticketsData?.count || tickets.length;
        const closedTickets = tickets.filter((t: any) => t.status === "closed");
        const memberSince = contact.createdAt
          ? format(parseISO(contact.createdAt), "MMM/yy", { locale: ptBR })
          : "-";

        // Extrair campos personalizados do contato
        const customFields: { label: string; value: string }[] = [];
        if (contact.extraInfo && Array.isArray(contact.extraInfo)) {
          contact.extraInfo.forEach((info: any) => {
            if (info.name && info.value) {
              customFields.push({ label: info.name, value: info.value });
            }
          });
        }

        // Extrair tags do contato
        const tags: string[] = [];
        if (contact.tags && Array.isArray(contact.tags)) {
          contact.tags.forEach((tag: any) => {
            if (tag.name) tags.push(tag.name);
          });
        }

        setContactData({
          phone: contact.number || "",
          email: contact.email || "",
          company: contact.companyName || "",
          location: "",
          tags,
          notes: contact.notes || "",
          customFields,
          ticketHistory,
          stats: {
            totalConversations,
            avgResponseTime: "8min",
            memberSince,
            satisfaction: closedTickets.length > 0 ? "98%" : "-",
          },
        });
      } catch (err) {
        console.error("Erro ao buscar dados do contato:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchContactData();
  }, [conversation?.originalTicket?.contactId]);

  if (!conversation || !isOpen) return null;

  const ChannelIcon = channelIcons[conversation.channel].icon;

  return (
    <motion.div
      initial={{ x: 380, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 380, opacity: 0 }}
      transition={{ type: "spring", damping: 25, stiffness: 200 }}
      className="w-[380px] flex-shrink-0 border-l border-[var(--border-color)] bg-[var(--sidebar-bg)] overflow-y-auto transition-colors duration-300"
    >
      {/* Header */}
      <div className="sticky top-0 z-10 flex items-center justify-between border-b border-[var(--border-color)] bg-[var(--sidebar-bg)] px-5 py-4">
        <h2 className="text-base font-semibold text-[var(--text-primary)]">{i18n.t("v2.contactPanel.title")}</h2>
        <button
          onClick={onClose}
          className="rounded-lg p-1.5 text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-primary)]"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Profile Section */}
      <div className="border-b border-[#E2E8F0] p-5">
        <div className="flex items-center gap-4">
          <div className="relative">
            <img
              src={conversation.clientAvatar}
              alt={conversation.clientName}
              className="h-16 w-16 rounded-full object-cover ring-4 ring-[#F1F5F9]"
            />
            <div
              className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full border-2 border-white"
              style={{ backgroundColor: channelIcons[conversation.channel].color }}
            >
              <ChannelIcon className="h-3 w-3 text-white" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold text-[#0F172A] truncate">
                {conversation.clientName}
              </h3>
              <button className="text-[#CBD5E1] hover:text-amber-400">
                <Star className="h-4 w-4" />
              </button>
            </div>
            <p className="text-sm text-[#64748B]">{contactData.company}</p>
            <div className="mt-1 flex items-center gap-1">
              <span className="h-2 w-2 rounded-full bg-[#22C55E]" />
              <span className="text-xs text-[#64748B]">{i18n.t("v2.contactPanel.onlineNow")}</span>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-5 flex gap-2">
          <button className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-[#E2E8F0] py-2.5 text-sm font-medium text-[#64748B] transition-colors hover:bg-[#F8FAFC] hover:text-[#0F172A]">
            <Phone className="h-4 w-4" />
            {i18n.t("v2.contactPanel.call")}
          </button>
          <button className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-[#E2E8F0] py-2.5 text-sm font-medium text-[#64748B] transition-colors hover:bg-[#F8FAFC] hover:text-[#0F172A]">
            <Mail className="h-4 w-4" />
            {i18n.t("v2.contactPanel.email")}
          </button>
        </div>
      </div>

      {/* Contact Info */}
      <div className="border-b border-[#E2E8F0] p-5">
        <h4 className="mb-4 text-xs font-semibold uppercase tracking-wide text-[#94A3B8]">
          {i18n.t("v2.contactPanel.contactInfo")}
        </h4>
        <div className="space-y-3">
          <InfoRow icon={Phone} label={i18n.t("v2.contactPanel.phone")} value={contactData.phone} />
          <InfoRow icon={Mail} label={i18n.t("v2.contactPanel.emailLabel")} value={contactData.email} />
          <InfoRow icon={Building2} label={i18n.t("v2.contactPanel.company")} value={contactData.company} />
          <InfoRow icon={MapPin} label={i18n.t("v2.contactPanel.location")} value={contactData.location} />
        </div>
      </div>

      {/* Tags */}
      <div className="border-b border-[#E2E8F0] p-5">
        <div className="mb-3 flex items-center justify-between">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-[#94A3B8]">
            {i18n.t("v2.contactPanel.tags")}
          </h4>
          <button className="rounded-lg p-1 text-[#2563EB] hover:bg-blue-50">
            <Plus className="h-4 w-4" />
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {contactData.tags.map((tag) => (
            <span
              key={tag}
              className={cn(
                "flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium",
                tagColors[tag]?.bg || "bg-gray-100",
                tagColors[tag]?.text || "text-gray-600"
              )}
            >
              <Tag className="h-3 w-3" />
              {tag}
            </span>
          ))}
        </div>
      </div>

      {/* Notes */}
      <div className="border-b border-[#E2E8F0] p-5">
        <div className="mb-3 flex items-center justify-between">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-[#94A3B8]">
            {i18n.t("v2.contactPanel.notes")}
          </h4>
          <button className="text-xs font-medium text-[#2563EB] hover:underline">
            {i18n.t("v2.contactPanel.edit")}
          </button>
        </div>
        <p className="text-sm text-[#64748B] leading-relaxed">
          {contactData.notes || i18n.t("v2.contactPanel.noNotes")}
        </p>
      </div>

      {/* Custom Fields */}
      <div className="border-b border-[#E2E8F0] p-5">
        <h4 className="mb-4 text-xs font-semibold uppercase tracking-wide text-[#94A3B8]">
          {i18n.t("v2.contactPanel.customFields")}
        </h4>
        <div className="space-y-3">
          {contactData.customFields.map((field) => (
            <div key={field.label} className="flex items-center justify-between">
              <span className="text-sm text-[#64748B]">{field.label}</span>
              <span className="text-sm font-medium text-[#0F172A]">{field.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Ticket History */}
      <div className="p-5">
        <div className="mb-4 flex items-center justify-between">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-[#94A3B8]">
            {i18n.t("v2.contactPanel.ticketHistory")}
          </h4>
          <button className="text-xs font-medium text-[#2563EB] hover:underline">
            {i18n.t("v2.contactPanel.viewAll")}
          </button>
        </div>
        <div className="space-y-3">
          {contactData.ticketHistory.map((ticket) => (
            <motion.div
              key={ticket.id}
              whileHover={{ x: 4 }}
              className="group flex cursor-pointer items-center justify-between rounded-xl bg-[#F8FAFC] p-3 transition-colors hover:bg-[#F1F5F9]"
            >
              <div className="flex items-center gap-3">
                <div
                  className={cn(
                    "flex h-8 w-8 items-center justify-center rounded-lg",
                    ticket.status === "resolved"
                      ? "bg-green-50 text-[#22C55E]"
                      : "bg-amber-50 text-[#F59E0B]"
                  )}
                >
                  <FileText className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-sm font-medium text-[#0F172A]">{ticket.id}</p>
                  <p className="text-xs text-[#64748B] truncate max-w-[180px]">
                    {ticket.title}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="text-right">
                  <span
                    className={cn(
                      "text-xs font-medium",
                      ticket.status === "resolved" ? "text-[#22C55E]" : "text-[#F59E0B]"
                    )}
                  >
                    {ticket.status === "resolved" ? i18n.t("v2.contactPanel.resolved") : i18n.t("v2.contactPanel.openStatus")}
                  </span>
                  <p className="text-xs text-[#94A3B8]">{ticket.date}</p>
                </div>
                <ChevronRight className="h-4 w-4 text-[#94A3B8] opacity-0 group-hover:opacity-100" />
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="border-t border-[#E2E8F0] p-5">
        <h4 className="mb-4 text-xs font-semibold uppercase tracking-wide text-[#94A3B8]">
          {i18n.t("v2.contactPanel.statistics")}
        </h4>
        <div className="grid grid-cols-2 gap-3">
          <StatBox icon={MessageCircle} label={i18n.t("v2.contactPanel.conversations")} value={String(contactData.stats.totalConversations)} color="#2563EB" />
          <StatBox icon={Clock} label={i18n.t("v2.contactPanel.avgTime")} value={contactData.stats.avgResponseTime} color="#22C55E" />
          <StatBox icon={Calendar} label={i18n.t("v2.contactPanel.memberSince")} value={contactData.stats.memberSince} color="#8B5CF6" />
          <StatBox icon={User} label={i18n.t("v2.contactPanel.satisfaction")} value={contactData.stats.satisfaction} color="#F59E0B" />
        </div>
      </div>
    </motion.div>
  );
}

interface InfoRowProps {
  icon: React.ElementType;
  label: string;
  value: string;
}

function InfoRow({ icon: Icon, label, value }: InfoRowProps) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#F8FAFC]">
        <Icon className="h-4 w-4 text-[#64748B]" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-[#94A3B8]">{label}</p>
        <p className="text-sm text-[#0F172A] truncate">{value}</p>
      </div>
    </div>
  );
}

interface StatBoxProps {
  icon: React.ElementType;
  label: string;
  value: string;
  color: string;
}

function StatBox({ icon: Icon, label, value, color }: StatBoxProps) {
  return (
    <div className="rounded-xl bg-[#F8FAFC] p-3">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4" style={{ color }} />
        <span className="text-lg font-bold text-[#0F172A]">{value}</span>
      </div>
      <p className="mt-0.5 text-xs text-[#64748B]">{label}</p>
    </div>
  );
}